import React from 'react'
import { Element } from 'react-bulma-components'
import { AvailableWorker, User } from '../../../api/users'
import { WorkersRequest, WorkersRequestPost } from '../../../api/workers-requests'
import {
  checkAvailibilityInPeriod,
  getWorkerAbsentLabel,
  getUserLabel,
  getWorkerLaborCostCoefficientKind,
} from '../../../utils/users'
import { compareDocsById } from '../../../utils/documents'
import { truncate } from 'lodash'
import { formatDate } from '../../../utils/date'
import { Employer } from '../../../api/organizations'
import WorkerLaborCostCoefficientKindLabel from '../../users/worker-labor-cost-coefficient-kind-label'

/**
 * Full mode: used when rendering a worker inside the context of a workers request
 * (assign form, multiple-assigns form). Uses `user.laborCostCoefficients` (populated
 * on AvailableWorker) and can display availability, desiredness, last mission date.
 */
interface WorkerInputLabelFullProps {
  user: AvailableWorker
  post?: WorkersRequestPost
  workersRequest: WorkersRequest
  isDesiredWorker?: boolean
  lastMissionStartDate?: Date | null
  employer?: undefined
  interimAgencyId?: undefined
}

/**
 * Compact mode: used when rendering a worker inside the DesiredWorkersField of the
 * workers-request-form. Only the labor-cost-coefficient kind is shown, computed via
 * `getWorkerLaborCostCoefficientKind` because the `User` type is not guaranteed to
 * carry the pre-computed `laborCostCoefficients` array.
 */
interface WorkerInputLabelCompactProps {
  user: User
  employer: Employer
  interimAgencyId?: string
  workersRequest?: undefined
  post?: undefined
  isDesiredWorker?: undefined
  lastMissionStartDate?: undefined
}

type WorkerInputLabelProps = WorkerInputLabelFullProps | WorkerInputLabelCompactProps

/**
 * React component that generates a formatted label for a worker.
 *
 * Two rendering modes:
 * - Full: when `workersRequest` is provided, all the contextual hints are shown
 *   (labor coefficient, desired-by-employer flag, absence notice, last mission,
 *   internal comments).
 * - Compact: when `employer` + `interimAgencyId` are provided, only the user label
 *   plus the labor-cost coefficient kind are displayed. This is used in the
 *   "desired workers" autocomplete of the workers-request-form.
 */
const WorkerInputLabel: React.FC<WorkerInputLabelProps> = props => {
  const { user } = props
  const userLabel = getUserLabel(user)

  // Compact mode: no workers-request context available.
  if (!props.workersRequest) {
    const laborCostCoefficientKind =
      props.interimAgencyId &&
      getWorkerLaborCostCoefficientKind(user, props.interimAgencyId, props.employer)

    return (
      <>
        {userLabel}
        {laborCostCoefficientKind && (
          <Element renderAs="small">
            {' '}
            -{' '}
            <WorkerLaborCostCoefficientKindLabel
              laborCostCoefficientKind={laborCostCoefficientKind}
            />
          </Element>
        )}
      </>
    )
  }

  // Full mode: rich contextual labelling.
  const { workersRequest, post, isDesiredWorker, lastMissionStartDate } = props

  const isAvailableForWorkersRequest = checkAvailibilityInPeriod(
    new Date(workersRequest.missionsStart),
    new Date(workersRequest.missionsEnd),
    user.availability,
  )
  const absentLabel =
    !isAvailableForWorkersRequest && getWorkerAbsentLabel(user.availability, user, true)

  const laborCostCoefficientKind =
    user.laborCostCoefficients.find(coef => compareDocsById(coef.employer, workersRequest.employer))
      ?.kind === 'management'
      ? 'Gestion'
      : 'Délégation'

  const employerRelation = user.employerRelations.find(relation =>
    compareDocsById(relation.employer, workersRequest.employer),
  )

  const isEmployerCollaborationInactive = employerRelation?.collaboration?.isInactive

  const isDesired =
    isDesiredWorker ||
    (post && post.desiredWorkers?.some(desiredWorker => desiredWorker?._id === user._id))

  return (
    <Element renderAs="span">
      {userLabel}
      <Element renderAs="small"> - {laborCostCoefficientKind}</Element>
      <Element textSize={7}>
        {isDesired && <Element textColor="success">Désiré pour ce poste par l'EU</Element>}
        {isEmployerCollaborationInactive && (
          <Element textColor="danger">
            L'employeur a indiqué qu'il ne voulait plus travailler avec cet intérimaire
          </Element>
        )}
        {absentLabel && <Element textColor="warning">Absent {absentLabel.toLowerCase()}</Element>}
        {lastMissionStartDate && (
          <Element textColor="grey">
            Dernière mission pour ce poste le {formatDate(lastMissionStartDate)}
          </Element>
        )}
        {user.internalComment && (
          <Element>{truncate(user.internalComment, { length: 70 })}</Element>
        )}
        {user.internalInformation && (
          <Element>{truncate(user.internalInformation, { length: 70 })}</Element>
        )}
      </Element>
    </Element>
  )
}

export default WorkerInputLabel
